import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { getUserEmail } from "../store/user/selectors.js";
import { getSearch } from "../store/search/selectors.js";
import useGlobalContext from "./useGlobalContext.js";
import useTrackerContext from "./useTrackerContext.js";
export function getCspNonce() {
    const el = document.querySelector("script[nonce]");
    if (!el)
        return "";
    return el.nonce || el.getAttribute("nonce") || "";
}
export async function sha256Hex(input) {
    const encoder = new TextEncoder();
    const data = encoder.encode(input);
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}
export function getDynamicSessionId(etStateBlob) {
    if (!etStateBlob)
        return "";
    const seedHex = Array.from(new TextEncoder().encode(etStateBlob))
        .map((byte) => byte.toString(16).padStart(2, "0"))
        .join("");
    const epochSeconds = Math.floor(Date.now() / 1000);
    const timestamp = epochSeconds - (epochSeconds % 1800);
    return seedHex + "_" + timestamp;
}
function useAdTechCommonData() {
    const { ipCountry, lang, etStateBlob } = useGlobalContext();
    const email = useSelector(getUserEmail) || "igi@example.com";
    return useCallback(async () => ({
        userLocation: ipCountry || "",
        userLanguage: lang || "",
        hashedEmail: email ? await sha256Hex(email) : "",
        dynamicSessionId: getDynamicSessionId(etStateBlob || "")
    }), [ipCountry, lang, email, etStateBlob]);
}
function useAdTechSearchParams() {
    const search = useSelector(getSearch);
    const segments = search.searchSegments;
    const isMultiStop = search.type === "MULTISTOP";
    const lastSegment = segments[segments.length - 1];
    const firstSegment = segments[0];
    return useMemo(() => {
        const departureDate = firstSegment?.departureDate || "";
        let arrivalDate = "";
        if (search.type === "ROUNDTRIP") {
            arrivalDate = firstSegment?.returnDate || "";
        }
        else if (isMultiStop && lastSegment) {
            arrivalDate = lastSegment.departureDate || "";
        }
        else if (search.type === "ONEWAY") {
            arrivalDate = firstSegment?.departureDate || "";
        }
        const sourceLocation = firstSegment?.from?.[0];
        const destinationSegment = isMultiStop ? lastSegment : firstSegment;
        const destinationLocation = destinationSegment?.to?.[0];
        return {
            departureDate,
            arrivalDate,
            adults: search.adults,
            children: search.children.length,
            sourceLocationName: sourceLocation?.name || "",
            sourceLocationCode: sourceLocation?.code || "",
            sourceLocationType: sourceLocation?.type || "",
            destinationLocationName: destinationLocation?.name || "",
            destinationLocationCode: destinationLocation?.code || "",
            destinationLocationType: destinationLocation?.type || "",
            tripType: search.type,
            cabinClass: search.cabinClass,
            directOnly: search.isDirect
        };
    }, [search, firstSegment, lastSegment, isMultiStop]);
}
export function useAdTechIndexTag() {
    const { trackExternal } = useTrackerContext();
    const { features } = useGlobalContext();
    const getCommonData = useAdTechCommonData();
    return useCallback(async () => {
        if (features.DATA_MEASUREMENT_FLIGHTS_WEB_GTM_TAGS) {
            const commonData = await getCommonData();
            trackExternal({
                type: "gtm-adtech",
                args: [
                    {
                        event: "flights-adtech-index",
                        ...commonData,
                        cspNonce: getCspNonce()
                    }
                ]
            });
        }
    }, [trackExternal, features, getCommonData]);
}
export function useAdTechSearchResultsTag() {
    const { trackExternal } = useTrackerContext();
    const { features } = useGlobalContext();
    const getCommonData = useAdTechCommonData();
    const searchParams = useAdTechSearchParams();
    return useCallback(async () => {
        if (features.DATA_MEASUREMENT_FLIGHTS_WEB_GTM_TAGS) {
            const commonData = await getCommonData();
            trackExternal({
                type: "gtm-adtech",
                args: [
                    {
                        event: "flights-adtech-search-results",
                        ...commonData,
                        cspNonce: getCspNonce(),
                        ...searchParams
                    }
                ]
            });
        }
    }, [trackExternal, features, getCommonData, searchParams]);
}
export function useAdTechOfferViewedTag() {
    const { trackExternal } = useTrackerContext();
    const { features } = useGlobalContext();
    const getCommonData = useAdTechCommonData();
    const searchParams = useAdTechSearchParams();
    return useCallback(async (offerData) => {
        if (features.DATA_MEASUREMENT_FLIGHTS_WEB_GTM_TAGS) {
            const commonData = await getCommonData();
            trackExternal({
                type: "gtm-adtech",
                args: [
                    {
                        event: "flights-adtech-offer-viewed",
                        ...commonData,
                        cspNonce: getCspNonce(),
                        ...searchParams,
                        ...offerData
                    }
                ]
            });
        }
    }, [trackExternal, features, getCommonData, searchParams]);
}
export function useAdTechCheckoutInitiatedTag() {
    const { trackExternal } = useTrackerContext();
    const { features } = useGlobalContext();
    const getCommonData = useAdTechCommonData();
    const searchParams = useAdTechSearchParams();
    return useCallback(async (offerData) => {
        if (features.DATA_MEASUREMENT_FLIGHTS_WEB_GTM_TAGS) {
            const commonData = await getCommonData();
            trackExternal({
                type: "gtm-adtech",
                args: [
                    {
                        event: "flights-adtech-checkout-initiated",
                        ...commonData,
                        cspNonce: getCspNonce(),
                        ...searchParams,
                        ...offerData
                    }
                ]
            });
        }
    }, [trackExternal, features, getCommonData, searchParams]);
}
export function useAdTechBookingConfirmedTag() {
    const { trackExternal } = useTrackerContext();
    const { features } = useGlobalContext();
    const getCommonData = useAdTechCommonData();
    const searchParams = useAdTechSearchParams();
    return useCallback(async (confirmationData) => {
        if (features.DATA_MEASUREMENT_FLIGHTS_WEB_GTM_TAGS) {
            const commonData = await getCommonData();
            trackExternal({
                type: "gtm-adtech",
                args: [
                    {
                        event: "flights-adtech-booking-confirmed",
                        ...commonData,
                        cspNonce: getCspNonce(),
                        ...searchParams,
                        ...confirmationData
                    }
                ]
            });
        }
    }, [trackExternal, features, getCommonData, searchParams]);
}
